import { useEffect } from 'react';
import { useDispatch, useSelector, useStore, RootStateOrAny } from 'react-redux';
import { useRouter } from 'next/router';
import { mapPunchErrorToType, PUNCH_ERROR_TYPE, PUNCH_MACHINE_PATH } from '@src/components/hr/punchInMachine/constants';
import { cacheMachinePunchContext, MachinePunchContext } from '@src/utils/punch/machinePunchData';

import useToast from '@src/hooks/useToast';
import useTranslation from '@src/hooks/useTranslation';
import { reactLocalStorage } from '@src/utils/localStorage';
import * as offlinePunchQueue from '@src/db/offlinePunchQueue';
import { SUCCESS_REMOVE_FALLBACK_MS } from '@src/utils/punch/offlinePunchScheduler';
import { DEVICE_ABNORMAL_MESSAGE_KEY, isDeviceAbnormalErrorType } from '@src/utils/punch/deviceAbnormality';
import { getUserId } from '@src/store/selectors/user';
import {
	getPunchInCall,
	clearPunchInRetryOutcome,
	setAsyncPunchErrorModal,
	clearAsyncPunchErrorModal,
	clearAsyncPunchInteractionLock,
	PunchInRetryOutcome,
	setMachinePunchError,
} from '@src/store/actions/punchIn';

// 消化 scheduler / firestorePunchWatcher push 進來的 outcome（local queue 重送 + 非同步打卡共用同一條鏈）。
// 兩個判斷軸分開：
//   - allowModal 決定文案家族：true = 前景非同步打卡（情境 A「打卡成功/打卡失敗」）；false = local queue 重送（情境 B「離線打卡上傳成功/失敗」）
//   - isHomePage 只在「情境 A 失敗」時決定 modal（停留首頁）還是 toast（未停留首頁）
// success 一律先 toast，再 getPunchInCall 回新 actual_*_time 後才清 queue 紀錄（避免 optimistic 閃爍）；
// 另有 fallback timer 保底 remove（getPunchInEpic 是 exhaustMap，onSuccess 可能被丟）。
// 限定 /hr/*（enabled）；enabled 進 deps → 離開 /hr 期間到達的 outcome，回到 /hr 時能補跑一次。
export default function usePunchInRetryOutcomeListener(enabled: boolean, isHomePage = false): void {
	const dispatch = useDispatch();
	const reduxStore = useStore();
	const toast = useToast();
	const { t } = useTranslation();
	const router = useRouter();
	const punchInRetryOutcomes: PunchInRetryOutcome[] = useSelector(
		(state: RootStateOrAny) => state.punchIn.punchInRetryOutcomes,
	) || [];
	const asyncPunchErrorModal = useSelector(
		(state: RootStateOrAny) => state.punchIn.asyncPunchErrorModal,
	);
	const currentUserId = useSelector(getUserId) as string | null;

	useEffect(() => {
		if (!enabled || !currentUserId) return;
		// Redux store 跨 route 存活；切帳後先清掉前一帳號尚未關閉的可操作 modal，
		// 絕不能讓 B 用自己的登入狀態重送 A 的 immutable context。
		if (
			asyncPunchErrorModal &&
			asyncPunchErrorModal.retryContext.accountId !== currentUserId
		) {
			dispatch(clearAsyncPunchErrorModal(asyncPunchErrorModal.id));
			return;
		}
		if (punchInRetryOutcomes.length === 0) return;
		const outcome = punchInRetryOutcomes[0];
		const { id, accountId, success, allowModal, asyncError, payload, retryContext } = outcome;
		if (accountId !== currentUserId) {
			// Outcome 仍 dispatch 到發起時捕捉的 store，才能在離開 /hr 後保留；若期間切帳，
			// 到達新帳號的 /hr 時靜默丟棄，且不阻塞該帳號自己的 FIFO。
			dispatch(clearAsyncPunchInteractionLock(id));
			dispatch(clearPunchInRetryOutcome(id));
			return;
		}
		// Bridge owner 使用預產生的 queue record id。terminal 可能快到讓 IDB selector
		// 從未觀察到 pending key；先做 id-guard clear，避免成功／失敗已呈現後仍鎖滿 30 秒。
		dispatch(clearAsyncPunchInteractionLock(id));
		if (outcome.punchKind === 'machine') {
			if (success) {
				if (allowModal === false) toast.open(t('hr:attendance:punch_in:queued_punch_uploaded'));
				dispatch(getPunchInCall({
					configs: { suppressErrorToast: true, cacheAccountId: accountId },
					onSuccess: (data: MachinePunchContext) => {
						if (getUserId(reduxStore.getState()) !== accountId) return;
						cacheMachinePunchContext(accountId, data)['catch'](() => { /* Cache is optional. */ });
					},
				}));
				// Keep the completed snapshot long enough for the mounted result/log page to consume it.
				setTimeout(() => {
					offlinePunchQueue.remove(id)['catch'](() => { /* TTL fallback */ });
				}, SUCCESS_REMOVE_FALLBACK_MS);
			} else if (outcome.response || outcome.asyncError) {
				const isEditing = typeof document !== 'undefined' && !!document.getElementById('edit-punch-modal');
				if (isHomePage || router.pathname === PUNCH_MACHINE_PATH || isEditing) {
					// Worker 的代碼與說明分開回傳；優先辨識代碼，通用代碼仍可由說明補足。
					const errorType = [
						outcome.response?.data?.error_type,
						asyncError?.errorType,
						asyncError?.code,
						outcome.response?.data?.raw,
						asyncError?.message,
					].map(value => mapPunchErrorToType({ response: { data: { error_type: value } } }))
						.find(value => value !== PUNCH_ERROR_TYPE.DEFAULT) || PUNCH_ERROR_TYPE.DEFAULT;
					dispatch(setMachinePunchError({ accountId, errorType }));
				} else toast.openWarning(t('hr:attendance:punch_in:clock_in_failed'));
			}
			dispatch(clearPunchInRetryOutcome(id));
			return;
		}

		if (success) {
			// 情境 A（前景非同步）成功一律「打卡成功」，不論是否停留首頁；情境 B（local queue 重送）用「離線打卡上傳成功」
			if (allowModal) {
				toast.open(payload?.duty ?
					t('hr:attendance:punch_in:clock_in_success') :
					t('hr:attendance:punch_in:clock_out_success'));
				reactLocalStorage.set('punchInAt', new Date(Date.now()).toISOString());
				// 與同步成功一致：記住上次成功的定位打卡地點，供 /hr 掛載時自動選回
				if (payload?.punch_in_type === 'location') {
					reactLocalStorage.set('selectLocationAt', new Date(Date.now()).toISOString());
					reactLocalStorage.set('lastPunchLocationId', String(payload.location_id ?? ''));
				}
			} else {
				toast.open(t('hr:attendance:punch_in:queued_punch_uploaded'));
			}
			dispatch(getPunchInCall({
				onSuccess: () => {
					offlinePunchQueue.remove(id)['catch'](() => { /* IDB 失敗不影響後續，下方 fallback timer 會兜底 */ });
				},
			}));
			// 保底 remove（冪等）：getPunchInEpic 是 exhaustMap，前一發在飛時上面那發連同 onSuccess 整包被丟（回 /hr 補跑時必發）
			setTimeout(() => {
				offlinePunchQueue.remove(id)['catch'](() => { /* IDB 不可用時略過，TTL 兜底 */ });
			}, SUCCESS_REMOVE_FALLBACK_MS);
		} else if (allowModal) {
			// 停留首頁 → 依 error_message 開對應特定 modal（IP/地點/QR，與同步 onError 同一套 whitelist）；
			// 未停留首頁 → 打卡失敗 toast
			if (isHomePage && retryContext) {
				// 可操作錯誤 modal 一次只呈現一筆。若 A 尚未關閉，B outcome 留在既有
				// punchInRetryOutcomes FIFO，不覆蓋 A；A 以 id-guard 清除後 effect 會再消化 B。
				if (asyncPunchErrorModal) return;
				dispatch(setAsyncPunchErrorModal({
					id,
					message: asyncError?.message,
					errorType: asyncError?.errorType,
					retryContext,
				}));
			} else if (isDeviceAbnormalErrorType(asyncError?.errorType)) {
				// 裝置異常要讓員工知道是企業設備限制，不能只顯示「打卡失敗」
				toast.openWarning(t(DEVICE_ABNORMAL_MESSAGE_KEY));
			} else {
				// 舊 outcome 沒有 immutable retry context 時不可開可操作 modal，避免拿 HRHome remount 後的預設 state 重打錯資料。
				toast.openWarning(t('hr:attendance:punch_in:clock_in_failed'));
			}
		} else {
			// 4xx / inactive→ 離線打卡上傳失敗 toast
			toast.openWarning(t('hr:attendance:punch_in:queued_punch_upload_failed'));
		}

		dispatch(clearPunchInRetryOutcome(id));
	}, [
		punchInRetryOutcomes, enabled, isHomePage, asyncPunchErrorModal, currentUserId, router.pathname, reduxStore,
	]);
}
